/**
 * Las cuentas del catálogo: cómo se llama una marca, y a cuánto queda un producto en oferta.
 *
 * Está copiado tal cual del sitio anterior a propósito. Son las reglas que el cliente ve en
 * pantalla y cualquier "mejora" acá cambia precios publicados.
 */

/**
 * El nombre corto es el que viaja en la base, en los códigos y en las URLs; el largo es el que
 * lee una persona. Sólo BENELLI difiere, y difiere porque CKING no vende Benelli: vende
 * artículos para Benelli, que no es lo mismo y es importante que no lo parezca.
 */
export const MARCAS = ['HADA', 'HAFEEN', 'VTC', 'BENELLI'] as const;

export type Marca = (typeof MARCAS)[number];

const NOMBRES: Record<string, string> = {
  HADA: 'HADA',
  HAFEEN: 'HAFEEN',
  VTC: 'VTC',
  BENELLI: 'ARTICULOS PARA BENELLI',
};

export function nombreDeMarca(marca: string | null | undefined): string {
  if (!marca) return '';
  return NOMBRES[marca] ?? marca;
}

/**
 * El porcentaje de descuento que trae la planilla.
 *
 * Viene escrito de las dos maneras y las dos significan lo mismo: "0.1" es diez por ciento y
 * "10" o "10%" también. El corte en 1 es la regla que ya estaba.
 *
 * Sale redondeado a centésimos de punto (7,25%) y nunca con basura de punto flotante: 0.07 * 100
 * en JavaScript es 7.000000000000001, y eso llegó a la tarjeta de 54 productos. Cien por ciento o
 * más es un error de tipeo en la planilla, no un descuento: aplicado daría un precio negativo.
 */
export function porcentajeDeDescuento(descuento: string | null | undefined): number | null {
  const puntos = puntosDeDescuento(descuento);
  return puntos === null ? null : puntos / 100;
}

/**
 * El descuento en centésimos de punto: "0.07" es 700, "7.5" es 750.
 *
 * Un entero, para que la cuenta de la oferta se haga en enteros. Es la misma regla que usa la
 * lista de precios en Excel del servidor (`ExcelFields.DiscountBasisPoints`): si una cambia, la
 * otra también, o la web y la planilla muestran precios distintos.
 */
function puntosDeDescuento(descuento: string | null | undefined): number | null {
  if (!descuento) return null;

  const sinSigno = String(descuento).replaceAll('%', '').trim();

  // La coma es el punto decimal cuando no hay punto: "0,15" es como sale un descuento de una
  // planilla escrita en Argentina. Igual que ExcelFields.DiscountNumber.
  const limpio = sinSigno.includes('.') ? sinSigno : sinSigno.replaceAll(',', '.');

  // Un número y nada más. parseFloat leía "5 u" como 5, y la lista de precios del servidor, que
  // no lo acepta, mostraba otro precio.
  if (!/^(\d+\.?\d*|\.\d+)$/.test(limpio)) return null;

  const numero = Number(limpio);
  if (numero <= 0 || numero >= 100) return null;

  const puntos = Math.round(numero < 1 ? numero * 10000 : numero * 100);
  return puntos > 0 && puntos < 10000 ? puntos : null;
}

/**
 * El precio en centavos, redondeando la mitad para arriba.
 *
 * Sobre la escritura decimal del número y no sobre su valor binario: 4.765 en binario es
 * 4,76499…, y redondeado así bajaría a 4,76 donde la lista de precios del servidor, que hace la
 * cuenta en decimal, dice 4,77. Quince cifras significativas es lo que un double guarda con
 * certeza; lo que viene después es ruido de la conversión.
 */
function aCentavos(valor: number): number {
  const signo = valor < 0 ? -1 : 1;
  const absoluto = Math.abs(valor);
  const texto = absoluto.toPrecision(15);

  // Notación científica sólo para números que no son precios. Ahí alcanza con la cuenta común.
  if (texto.includes('e')) return signo * Math.round(absoluto * 100);

  const [entero, fraccion = ''] = texto.split('.');
  const centavos = Number(entero) * 100 + Number(fraccion.padEnd(2, '0').slice(0, 2));

  return signo * (centavos + (fraccion.charAt(2) >= '5' ? 1 : 0));
}

/**
 * El precio con el descuento aplicado, en centavos exactos.
 *
 * Se descuenta sobre el precio de lista redondeado, que es el que se ve: la planilla trae precios
 * como 19.398823555664542, se muestra 19,40, y la oferta tiene que ser la cuenta que el cliente
 * puede hacer con lo que tiene delante. La cuenta va en enteros y el redondeo es la mitad para
 * arriba: en punto flotante 19,95 menos 10% daba 17,95 y no 17,96.
 */
export function precioConDescuento(
  precio: number | null | undefined,
  porcentaje: number | null,
): number | null {
  if (precio === null || precio === undefined || porcentaje === null) return null;

  const lista = aCentavos(precio);
  const puntos = Math.round(porcentaje * 100);

  // lista * (10000 - puntos) es un entero; sumarle la mitad del divisor y quitar el resto es
  // redondear la mitad para arriba sin salir de los enteros.
  const bruto = lista * (10000 - puntos) + 5000;
  return (bruto - (bruto % 10000)) / 10000 / 100;
}

/** El precio como se lee en Argentina, con el mismo redondeo que usa la cuenta de la oferta. */
export function enPesos(valor: number): string {
  return (aCentavos(valor) / 100).toLocaleString('es-AR', { style: 'currency', currency: 'ARS' });
}

/**
 * Si el producto está en oferta: lo dice el estado, o tiene un descuento que se aplica.
 *
 * Un descuento es una oferta. Treinta y cinco productos tenían descuento sin OFERTA en el estado:
 * la ficha los mostraba rebajados y la tarjeta no, ni la etiqueta ni el precio tachado. La
 * planilla ya no deja subir ese caso; esto es para que lo que ya está se vea igual en todos lados.
 */
export function estaEnOferta(producto: {
  is_offer: boolean;
  is_no_discount: boolean;
  discount: string | null;
}): boolean {
  if (producto.is_offer) return true;
  return !producto.is_no_discount && porcentajeDeDescuento(producto.discount) !== null;
}

/**
 * Cómo se muestra el precio de un producto: con descuento o sin él.
 *
 * Las cuatro pantallas que muestran un precio —la tarjeta, la tabla, la ficha y la vista rápida—
 * hacían estas dos cuentas cada una por su lado, con el mismo comentario copiado encima. Las
 * copias se separaron dos veces, y las dos se vieron en pantalla: primero la ficha mostró un
 * precio de oferta que no existía porque no miraba `is_no_discount`, y después la tarjeta dejó de
 * rebajar a treinta y cinco productos porque además del descuento pedía el estado "OFERTA".
 *
 * Acá hay una sola cuenta, y está probada.
 */
export type PrecioMostrado =
  | {
      /** El porcentaje, o null si este producto no lleva descuento. */
      descuento: number | null;
      /** El precio ya rebajado, o null si no hay descuento o no hay precio. */
      conOferta: number | null;
      mostrarOferta: false;
    }
  // Las dos son un número cuando hay oferta, y eso lo sabe también TypeScript: dentro de un
  // `if (mostrarOferta)` no hay que volver a preguntar si son null.
  | { descuento: number; conOferta: number; mostrarOferta: true };

export function precioParaMostrar(producto: {
  price: number | null;
  discount: string | null;
  is_no_discount: boolean;
}): PrecioMostrado {
  // Un producto marcado "NO DTO" o "PRECIO FINAL" no lleva descuento aunque la columna traiga un
  // porcentaje. Es la regla del negocio y está escrita así en la planilla.
  const descuento = producto.is_no_discount ? null : porcentajeDeDescuento(producto.discount);
  const conOferta = precioConDescuento(producto.price, descuento);

  // Con descuento se muestra rebajado, diga o no OFERTA el estado: un descuento es una oferta.
  return descuento !== null && conOferta !== null
    ? { descuento, conOferta, mostrarOferta: true }
    : { descuento, conOferta, mostrarOferta: false };
}

/** El porcentaje como se lee en Argentina: 7, 7,5 o 7,25. Sin el signo, que pone cada pantalla. */
export function enPorcentaje(porcentaje: number): string {
  return porcentaje.toLocaleString('es-AR', { maximumFractionDigits: 2 });
}

/** La columna de descuento por bulto trae una X cuando corresponde, y nada cuando no. */
export function tieneDescuentoPorBulto(valor: string | null | undefined): boolean {
  if (!valor) return false;
  return valor.trim().toUpperCase() === 'X';
}
